/**
 * SELECAO MULTIPLA · escolher VARIAS opcoes de uma lista, e cadastrar a que faltar.
 *
 * ===========================================================================
 * POR QUE ELA EXISTE, E POR QUE NAO E O `Selecao` COM UMA BANDEIRA
 * ===========================================================================
 * `Selecao` guarda UM valor (`value?: string`) e fecha ao escolher. Multipla escolha inverte
 * as duas coisas: o valor e uma lista, e a lista NAO fecha ao marcar, porque quem esta
 * marcando tres itens nao quer reabrir o campo duas vezes. Somar uma bandeira `multipla` ao
 * `Selecao` faria metade do corpo dele virar `if`, e as duas pecas passariam a envelhecer
 * juntas sem precisar.
 *
 * ===========================================================================
 * O CADASTRO DO QUE FALTA E A METADE MAIS IMPORTANTE
 * ===========================================================================
 * Decisao do fundador em enquete, 13/09/2026:
 *
 *   "caso ele nao ache na lista devera ter opcao para o usuario cadastrar, entao a lista
 *    sera por medico."
 *
 * UMA LISTA OFICIAL QUE NAO ACEITA O QUE FALTA MENTE DUAS VEZES: primeiro quando obriga a
 * pessoa a escolher o item errado por ser o mais parecido, e depois quando alguem conta os
 * dados acreditando na lista. Por isso o cadastro NAO e um caminho escondido em outra tela:
 * ele aparece dentro da propria busca, no instante em que ela nao acha nada, que e
 * exatamente o instante em que a pessoa precisa dele.
 *
 * ===========================================================================
 * O DESENHO, e cada decisao tem um porque
 * ===========================================================================
 * · OS ESCOLHIDOS FICAM ACIMA, como marcadores removiveis, e nao como texto no campo. Com
 *   cinco especialidades, o texto truncaria e a pessoa deixaria de ver o que escolheu.
 * · A BUSCA FICA DENTRO da lista aberta, e nao fora: com vinte opcoes, rolar e pior que
 *   digitar tres letras.
 * · A LISTA NAO FECHA AO MARCAR. Ela fecha por clique fora, pelo Esc, ou pelo botao.
 * · ALVO DE 44px em tudo que se toca, que e o minimo da Apple e a regra desta plataforma.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { t } from '@complexity/i18n';

/** Uma opcao da lista. `propria` marca a que o consultorio cadastrou, e nao a da lista base. */
export interface OpcaoMultipla {
  readonly valor: string;
  readonly rotulo: string;
  /**
   * VEIO DO CONSULTORIO, e nao da lista oficial.
   *
   * A TELA PRECISA DISTINGUIR as duas, e nao e detalhe: quem le "Cardiologia" ao lado de uma
   * especialidade que alguem digitou ontem precisa saber qual das duas o conselho reconhece.
   */
  readonly propria?: boolean;
}

export interface SelecaoMultiplaProps {
  readonly rotulo: string;
  readonly opcoes: readonly OpcaoMultipla[];
  /** Os valores escolhidos. Lista vazia e "nenhum", e nunca nulo: ausencia aqui nao existe. */
  readonly valores: readonly string[];
  readonly aoMudar: (valores: readonly string[]) => void;
  /**
   * CADASTRAR O QUE NAO ESTA NA LISTA. Ausente, o campo vira so escolha, e o botao de
   * cadastrar nao aparece: prometer um cadastro que ninguem trata seria pior que nao ter.
   */
  readonly aoCadastrar?: (rotulo: string) => void;
  readonly cadastrando?: boolean;
  readonly disabled?: boolean;
  readonly obrigatorio?: boolean;
  readonly erro?: string;
  readonly ajuda?: string;
  /** O que o campo diz quando ninguem escolheu nada. */
  readonly vazio?: string;
}

/** O X dos marcadores, desenhado e nao emoji, pela regra dura da plataforma. */
function IconeX({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
    </svg>
  );
}

/** O sinal de mais do cadastro, pelo mesmo motivo. */
function IconeMais({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

/** O visto da opcao marcada. */
function IconeVisto({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function SelecaoMultipla({
  rotulo,
  opcoes,
  valores,
  aoMudar,
  aoCadastrar,
  cadastrando,
  disabled,
  obrigatorio,
  erro,
  ajuda,
  vazio,
}: SelecaoMultiplaProps) {
  const id = useId();
  const idLista = `${id}-lista`;
  const idAjuda = `${id}-ajuda`;
  const idErro = `${id}-erro`;

  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  const raiz = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const campoBusca = useRef<HTMLInputElement>(null);

  /**
   * A COMPARACAO IGNORA CAIXA E ACENTO, e isso nao e conveniencia.
   *
   * Sem normalizar, quem digita "pediatria" nao acha "Pediatria", e quem digita "cardio" nao
   * acha "Cardiologia" se o acento estiver no meio. A pessoa conclui que a especialidade nao
   * existe e CADASTRA UMA DUPLICATA, que e justamente o defeito que a lista base evita.
   */
  const normalizar = (texto: string) =>
    texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const filtradas = useMemo(() => {
    const alvo = normalizar(busca);
    if (alvo === '') return opcoes;
    return opcoes.filter((o) => normalizar(o.rotulo).includes(alvo));
  }, [opcoes, busca]);

  // O rotulo de cada valor escolhido, para os marcadores. Valor sem opcao correspondente
  // mostra o proprio valor: sumir seria pior, porque a pessoa teria escolhido algo invisivel.
  const escolhidas = valores.map((v) => ({
    valor: v,
    rotulo: opcoes.find((o) => o.valor === v)?.rotulo ?? v,
  }));

  /**
   * O TEXTO DIGITADO JA EXISTE NA LISTA?
   *
   * E o que decide se o botao de cadastrar aparece. A comparacao e pelo texto NORMALIZADO e
   * contra a lista INTEIRA, e nao contra a filtrada: filtrada, um texto que casa parcialmente
   * esconderia o botao mesmo sem existir igual.
   */
  const jaExiste = opcoes.some((o) => normalizar(o.rotulo) === normalizar(busca));
  const podeCadastrar = aoCadastrar !== undefined && busca.trim() !== '' && !jaExiste;

  function alternar(valor: string) {
    // Marcar e desmarcar sao o mesmo gesto, e a lista nova e sempre NOVA: mutar a recebida
    // nao muda a referencia, o React nao redesenha, e o campo ficaria marcado so no estado.
    const nova = valores.includes(valor) ? valores.filter((v) => v !== valor) : [...valores, valor];
    aoMudar(nova);
  }

  function cadastrar() {
    const texto = busca.trim();
    if (texto === '' || aoCadastrar === undefined) return;
    aoCadastrar(texto);
    // A BUSCA SE LIMPA, porque o item recem cadastrado passa a aparecer na lista e a busca
    // filtrada por ele esconderia todo o resto.
    setBusca('');
  }

  // FECHAR AO CLICAR FORA. `pointerdown` e nao `click`, pelo mesmo motivo do `Selecao`: um
  // arraste que comeca dentro e termina fora fecharia o campo no meio do gesto.
  useEffect(() => {
    if (!aberto) return;
    function aoApontar(evento: PointerEvent) {
      if (!raiz.current?.contains(evento.target as Node)) setAberto(false);
    }
    document.addEventListener('pointerdown', aoApontar);
    return () => document.removeEventListener('pointerdown', aoApontar);
  }, [aberto]);

  // O FOCO VAI PARA A BUSCA ao abrir: quem abre uma lista de cinquenta itens vai digitar, e
  // obrigar um toque a mais no campo de busca e um gesto que a peca pode poupar.
  useEffect(() => {
    if (aberto) campoBusca.current?.focus();
  }, [aberto]);

  return (
    <div className="flex flex-col gap-cx-1" ref={raiz}>
      <span className="text-cx-sm font-medium text-texto-suave" id={`${id}-rotulo`}>
        {t(rotulo)}
        {obrigatorio && (
          <span aria-hidden="true" className="ml-cx-1 text-erro">
            *
          </span>
        )}
      </span>

      {/* OS MARCADORES DO QUE JA FOI ESCOLHIDO, acima do campo. Eles somem quando nao ha
          nenhum, em vez de deixar uma faixa vazia ocupando altura. */}
      {escolhidas.length > 0 && (
        <ul className="flex flex-wrap gap-cx-1" aria-label={t('Escolhidas')}>
          {escolhidas.map((e) => (
            <li key={e.valor}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => alternar(e.valor)}
                // O NOME ACESSIVEL DIZ O QUE O CLIQUE FAZ, e nao so o rotulo: um leitor de
                // tela anunciando "Cardiologia, botao" nao diz que tocar nele REMOVE.
                aria-label={`${t('Remover')} ${e.rotulo}`}
                className={[
                  'flex min-h-toque items-center gap-cx-1 rounded-cx-full border px-cx-2 text-cx-xs',
                  'border-marca/40 bg-marca/10 text-texto',
                  'hover:border-marca focus:outline-none focus:ring-2 focus:ring-marca/20',
                ].join(' ')}
              >
                <span className="truncate">{e.rotulo}</span>
                <IconeX className="h-3.5 w-3.5 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative">
        <button
          ref={gatilho}
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={aberto}
          aria-controls={aberto ? idLista : undefined}
          aria-labelledby={`${id}-rotulo ${id}`}
          aria-invalid={erro ? true : undefined}
          aria-describedby={[erro ? idErro : null, ajuda ? idAjuda : null].filter(Boolean).join(' ') || undefined}
          onClick={() => setAberto(!aberto)}
          className={[
            'flex min-h-toque w-full items-center gap-cx-2 rounded-cx-md border px-cx-3 text-left text-cx-base',
            'bg-superficie text-texto',
            erro ? 'border-erro' : 'border-borda-forte',
            disabled ? 'cursor-not-allowed text-texto-fraco' : 'hover:border-marca',
            'focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/20',
          ].join(' ')}
        >
          <span className="min-w-0 flex-1 truncate text-texto-fraco">
            {escolhidas.length === 0
              ? (vazio ?? t('Escolha uma ou mais'))
              : `${escolhidas.length} ${t('escolhida(s)')}`}
          </span>
        </button>

        {aberto && (
          <div
            className={[
              'absolute z-painel top-full mt-cx-2 w-full rounded-cx-md border border-vidro',
              'bg-vidro-grosso backdrop-blur-vidro backdrop-saturate-vidro shadow-cx-lg',
            ].join(' ')}
          >
            {/* A BUSCA, dentro da lista. `type=text` e nao `search`: o `search` desenha um X
                nativo que segue o tema do SISTEMA, e nao o da pagina. */}
            <div className="border-b border-vidro p-cx-2">
              <input
                ref={campoBusca}
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={t('Buscar')}
                aria-label={t('Buscar na lista')}
                className={[
                  'min-h-toque w-full rounded-cx-sm border border-borda bg-superficie px-cx-2 text-cx-sm text-texto',
                  'focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/20',
                ].join(' ')}
              />
            </div>

            <ul id={idLista} role="listbox" aria-multiselectable="true" className="max-h-[240px] overflow-y-auto">
              {filtradas.map((opcao) => {
                const marcada = valores.includes(opcao.valor);
                return (
                  <li
                    key={opcao.valor}
                    role="option"
                    aria-selected={marcada}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      alternar(opcao.valor);
                    }}
                    className={[
                      'flex min-h-toque cursor-pointer items-center gap-cx-2 px-cx-3 text-cx-sm',
                      marcada ? 'text-texto' : 'text-texto-suave',
                      'hover:bg-marca/10',
                    ].join(' ')}
                  >
                    {/* A CAIXA DE MARCA E DESENHADA, e nao um `input[type=checkbox]`: o
                        nativo segue o tema do sistema operacional, e a plataforma exige que
                        tudo troque com o tema da pagina. */}
                    <span
                      aria-hidden="true"
                      className={[
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-cx-sm border',
                        marcada ? 'border-marca bg-marca text-marca-contraste' : 'border-borda-forte',
                      ].join(' ')}
                    >
                      {marcada && <IconeVisto className="h-3.5 w-3.5" />}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{opcao.rotulo}</span>
                    {/* A PROCEDENCIA fica visivel: quem le precisa saber qual o conselho
                        reconhece e qual alguem digitou. */}
                    {opcao.propria && (
                      <span className="shrink-0 text-cx-xs text-texto-fraco">{t('do consultório')}</span>
                    )}
                  </li>
                );
              })}

              {/* A LISTA VAZIA DIZ UM FATO, e nao parece defeito do sistema: nao achar nada
                  numa busca e uma resposta, e nao uma falha. */}
              {filtradas.length === 0 && !podeCadastrar && (
                <li className="px-cx-3 py-cx-2 text-cx-sm text-texto-fraco">
                  {t('Nada encontrado.')}
                </li>
              )}
            </ul>

            {/* CADASTRAR O QUE NAO EXISTE, no instante em que a busca nao acha. */}
            {podeCadastrar && (
              <div className="border-t border-vidro p-cx-2">
                <button
                  type="button"
                  onClick={cadastrar}
                  disabled={cadastrando}
                  className={[
                    'flex min-h-toque w-full items-center gap-cx-2 rounded-cx-sm px-cx-2 text-left text-cx-sm',
                    'text-marca hover:bg-marca/10',
                    'focus:outline-none focus:ring-2 focus:ring-marca/20',
                    cadastrando ? 'cursor-wait opacity-60' : '',
                  ].join(' ')}
                >
                  <IconeMais className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    {cadastrando ? t('Cadastrando...') : `${t('Cadastrar')} "${busca.trim()}"`}
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {erro && (
        <p id={idErro} role="alert" className="text-cx-xs text-erro">
          {erro}
        </p>
      )}
      {ajuda && !erro && (
        <p id={idAjuda} className="text-cx-xs text-texto-suave">
          {ajuda}
        </p>
      )}
    </div>
  );
}
